import { useEffect, useState, useCallback } from 'react'
import { DataTable } from '../../components/DataTable'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { ticketsApi } from '@carrent/api'

const STATUS_FILTERS = [
  { value: 'active',      label: 'Активные', icon: '💬' },
  { value: 'open',        label: 'Открытые', icon: '📭' },
  { value: 'in_progress', label: 'В работе', icon: '🔄' },
  { value: 'waiting',     label: 'Ждут отв.', icon: '⏳' },
  { value: 'resolved',    label: 'Решено',   icon: '✅' },
  { value: 'closed',      label: 'Закрыты',  icon: '🔒' },
]

// Тона вместо hex: цвета приоритета обязаны меняться вместе с темой,
// а захардкоженный #ef4444 в тёмной теме бьёт по глазам.
// Порядок для сортировки: по названию приоритеты встают алфавитом,
// что для «Срочно» перед «Низкий» бессмысленно.
const PRIORITY_ORDER = ['low', 'normal', 'high', 'urgent']

const PRIORITY_STYLE = {
  low:    { tone: 'neutral', label: 'Низкий' },
  normal: { tone: 'info',    label: 'Обычн.' },
  high:   { tone: 'warning', label: 'Высок.' },
  urgent: { tone: 'danger',  label: 'Срочно' },
}

const TICKET_COLUMNS = [
  {
    key: 'id', label: '#', width: 'id', sortable: true, cellClassName: 'table__num',
    render: t => `#${t.id}`,
  },
  {
    key: 'subject', label: 'Тема', sortable: true,
    render: t => <span className="table__title">{t.subject}</span>,
  },
  {
    key: 'author', label: 'Автор', cellClassName: 'table__muted', sortable: true,
    sortValue: t => t.user?.full_name || '',
    render: t => t.user?.full_name || `Пользователь #${t.user_id}`,
  },
  {
    key: 'status', label: 'Статус', width: 'status', cellClassName: 'table__muted',
    render: t => t.status_label || t.status,
  },
  {
    key: 'priority', label: 'Приоритет', width: 'priority',
    sortValue: t => PRIORITY_ORDER.indexOf(t.priority),
    sortable: true,
    render: t => {
      const pr = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.normal
      return <span className={`badge badge--${pr.tone}`}>{pr.label}</span>
    },
  },
  {
    key: 'unread_count', label: 'Непрочит.', sortable: true,
    render: t => t.unread_count > 0
      ? <span className="badge badge--accent">{t.unread_count}</span>
      : <span className="table__muted">—</span>,
  },
]

export default function AdminTicketsPage() {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState([])
  const [counts, setCounts]   = useState({})
  const [searchParams] = useSearchParams()
  const [filter, setFilter]   = useState(() => searchParams.get('status') ?? 'active')
  // Приоритет отдельным срезом: гаджеты приводят сюда именно за срочными.
  const priority = searchParams.get('priority') || ''
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    ticketsApi.adminList({ status: filter, per_page: 50 })
      .then(r => {
        const items = r.data?.data?.items || []
        // Приоритет режем на клиенте: у adminList такого параметра нет,
        // а менять контракт ради одного среза несоразмерно.
        setTickets(priority ? items.filter(t => t.priority === priority) : items)
        setCounts(r.data?.data?.counts || {})
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [filter, priority])

  useEffect(load, [load])

  return (
    <div>
      <PageHeader title="Обращения" />

      {/* Фильтры */}
      <div style={{ padding: '0 var(--gap) var(--gap-sm)' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {STATUS_FILTERS.map(f => {
            const cnt = counts[f.value] || 0
            return (
              <button key={f.value}
                className={`btn ${filter === f.value ? 'btn--primary' : 'btn--outline'}`}
                style={{ flexShrink: 0, padding: '6px 12px', fontSize: '0.78rem',
                  display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={() => setFilter(f.value)}>
                {f.icon} {f.label}
                {cnt > 0 && (
                  <span style={{
                    background: filter === f.value ? 'rgba(255,255,255,0.25)' : 'var(--border)',
                    color: filter === f.value ? '#fff' : 'var(--text-muted)',
                    fontSize: '0.7rem', padding: '1px 6px',
                    borderRadius: 99, fontWeight: 700,
                  }}>{cnt}</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Срочные */}
        {counts.urgent > 0 && (
          <div style={{
            marginTop: 8, padding: '6px 10px',
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 'var(--r-sm)',
            color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 700,
            cursor: 'pointer',
          }} onClick={() => setFilter('open')}>
            🚨 {counts.urgent} срочных обращения требуют внимания
          </div>
        )}
      </div>

      <div className="p-page flex-col" style={{ gap: 'var(--gap-sm)', paddingTop: 0 }}>
        <DataTable
          columns={TICKET_COLUMNS}
          rows={tickets}
          loading={loading}
          onRowClick={t => navigate(`/admin/tickets/${t.id}`)}
          empty={{ icon: 'chat', title: 'Нет обращений в этой категории',
            text: 'Выберите другую категорию или снимите фильтр.' }}
        />
      </div>
    </div>
  )
}
